import type { IPlayer } from "@ihs-widget/components/StatsConsole/Widgets/LineupWidget/parts/Players";

export const getPlayerRows = (players: IPlayer[], formation: string | null) => {
    if (!players || !formation) {
        return { rows: [], rowLengths: [] };
    }
    // Filter only active players
    const activePlayers = players.filter(p => p.isCurrentlyOnField);
    
    // Split into players with and without positions
    const playersWithPosition = activePlayers.filter(p => p.position);
    const playersWithoutPosition = activePlayers.filter(p => !p.position);
    
    // Sort players by position
    const sortedPlayersWithPosition = playersWithPosition.sort((a, b) => {
        const positionOrder = {
            'goalkeeper': 1,
            'wing_back': 2,
            'left_wing_back': 2,
            'right_wing_back': 2,
            'central_wing_back': 2,
            'left_central_wing_back': 2,
            'right_central_wing_back': 2,
            'defender': 2,
            'left_defender': 2,
            'right_defender': 2,
            'central_defender': 2,
            'left_central_defender': 2,
            'right_central_defender': 2,
            'back': 2,
            'left_back': 2,
            'right_back': 2,
            'central_back': 2,
            'left_central_back': 2,
            'right_central_back': 2,
            'defensive_midfielder': 3,
            'midfielder': 3,
            'central_midfielder': 3,
            'left_midfielder': 3,
            'right_midfielder': 3,
            'left_central_midfielder': 3,
            'right_central_midfielder': 3,
            'attacking_midfielder': 4,
            'left_attacking_midfielder': 4,
            'right_attacking_midfielder': 4,
            'central_attacking_midfielder': 4,
            'left_central_attacking_midfielder': 4,
            'right_central_attacking_midfielder': 4,
            'left_winger': 4,
            'right_winger': 4,
            'forward': 5,
            'central_forward': 5,
            'left_central_forward': 5,
            'right_central_forward': 5,
            'left_forward': 5,
            'right_forward': 5,
            'striker': 5,
            'left_striker': 5,
            'right_striker': 5,
            'central_striker': 5,
            'left_central_striker': 5,
            'right_central_striker': 5,
        };
        return (positionOrder[a.position as keyof typeof positionOrder] || 0) 
             - (positionOrder[b.position as keyof typeof positionOrder] || 0);
    });

    const rows: IPlayer[][] = [];
    const rowLengths: number[] = [];

    // Goalkeeper always in the first row
    const goalkeeper = sortedPlayersWithPosition.find(p => p.position === 'goalkeeper');
    if (goalkeeper) {
        rows.push([goalkeeper]);
        rowLengths.push(1);
    } else if (playersWithoutPosition.length > 0) {
        // If there's no goalkeeper with position, use the first player without position
        const substituteGoalkeeper = playersWithoutPosition.shift();
        if (substituteGoalkeeper) {
            rows.push([substituteGoalkeeper]);
            rowLengths.push(1);
        }
    }

    // Process the formation
    const positions = formation.split('-').map(Number);
    let currentIndex = 1; // Start from 1 because goalkeeper is already added

    positions.forEach(playersInRow => {
        // Collect players with position for this row
        const playersForThisRow = sortedPlayersWithPosition
            .slice(currentIndex, currentIndex + playersInRow)
            .filter(p => p.position !== 'goalkeeper');
        
        // Check how many slots remain to be filled
        const remainingSlots = playersInRow - playersForThisRow.length;
        
        // If there are empty slots and we have players without positions, add them
        const rowPlayers = [...playersForThisRow];
        
        if (remainingSlots > 0 && playersWithoutPosition.length > 0) {
            const playersToAdd = playersWithoutPosition.splice(0, remainingSlots);
            rowPlayers.push(...playersToAdd);
        }
        
        if (rowPlayers.length > 0) {
            rows.push(rowPlayers);
            rowLengths.push(rowPlayers.length);
        }
        
        currentIndex += playersInRow;
    });

    return { rows, rowLengths };
};

export const getPlayerRowsAndAddEmptyIfNeeded = (players: IPlayer[], formation: string | null) => {
    if (!players || !formation) {
        return { rows: [], rowLengths: [] };
    }
    // Filter only active players
    const activePlayers = players.filter(p => p.isCurrentlyOnField);
    
    // Split into players with and without positions
    const playersWithPosition = activePlayers.filter(p => p.position);
    const playersWithoutPosition = activePlayers.filter(p => !p.position);
    
    // Sort players by position
    const sortedPlayersWithPosition = playersWithPosition.sort((a, b) => {
        const positionOrder = {
            'goalkeeper': 1,
            'wing_back': 2,
            'left_wing_back': 2,
            'right_wing_back': 2,
            'central_wing_back': 2,
            'left_central_wing_back': 2,
            'right_central_wing_back': 2,
            'defender': 2,
            'left_defender': 2,
            'right_defender': 2,
            'central_defender': 2,
            'left_central_defender': 2,
            'right_central_defender': 2,
            'back': 2,
            'left_back': 2,
            'right_back': 2,
            'central_back': 2,
            'left_central_back': 2,
            'right_central_back': 2,
            'defensive_midfielder': 3,
            'midfielder': 3,
            'central_midfielder': 3,
            'left_midfielder': 3,
            'right_midfielder': 3,
            'left_central_midfielder': 3,
            'right_central_midfielder': 3,
            'attacking_midfielder': 4,
            'left_attacking_midfielder': 4,
            'right_attacking_midfielder': 4,
            'central_attacking_midfielder': 4,
            'left_central_attacking_midfielder': 4,
            'right_central_attacking_midfielder': 4,
            'left_winger': 4,
            'right_winger': 4,
            'forward': 5,
            'central_forward': 5,
            'left_central_forward': 5,
            'right_central_forward': 5,
            'left_forward': 5,
            'right_forward': 5,
            'striker': 5,
            'left_striker': 5,
            'right_striker': 5,
            'central_striker': 5,
            'left_central_striker': 5,
            'right_central_striker': 5,
        };
        return (positionOrder[a.position as keyof typeof positionOrder] || 0) 
             - (positionOrder[b.position as keyof typeof positionOrder] || 0);
    });

    const rows: IPlayer[][] = [];
    const rowLengths: number[] = [];

    // Goalkeeper always in the first row
    const goalkeeper = sortedPlayersWithPosition.find(p => p.position === 'goalkeeper');
    if (goalkeeper) {
        rows.push([goalkeeper]);
        rowLengths.push(1);
    } else if (playersWithoutPosition.length > 0) {
        // If there's no goalkeeper with position, use the first player without position
        const substituteGoalkeeper = playersWithoutPosition.shift();
        if (substituteGoalkeeper) {
            rows.push([substituteGoalkeeper]);
            rowLengths.push(1);
        }
    }

    // Process the formation
    const positions = formation.split('-').map(Number);
    let currentIndex = 1; // Start from 1 because goalkeeper is already added

    positions.forEach(playersInRow => {
        // Collect players with position for this row
        const playersForThisRow = sortedPlayersWithPosition
            .slice(currentIndex, currentIndex + playersInRow)
            .filter(p => p.position !== 'goalkeeper');
        
        // Check how many slots remain to be filled
        const remainingSlots = playersInRow - playersForThisRow.length;
        
        // If there are empty slots and we have players without positions, add them
        const rowPlayers = [...playersForThisRow];
        
        if (remainingSlots > 0 && playersWithoutPosition.length > 0) {
            const playersToAdd = playersWithoutPosition.splice(0, remainingSlots);
            rowPlayers.push(...playersToAdd);
        }
        
        if (rowPlayers.length > 0) {
            rows.push(rowPlayers);
            rowLengths.push(rowPlayers.length);
        }
        
        currentIndex += playersInRow;
    });

    // Calculate the required number of rows
    const requiredRows = formation.split('-').length + 1;

    // Add empty arrays if needed
    while (rows.length < requiredRows) {
        rows.push([]);
        rowLengths.push(0);
    }

    return { rows, rowLengths };
};